import React, { ChangeEvent, useEffect, useRef, useState } from 'react'
import styled from '@emotion/styled'
import { useAppDispatch, useAppSelector } from '../../state/hooks'
import {
  selectCreativeDrawerData,
  selectCreativeDrawerOpen,
  selectUserKey,
  setCampaignModalOpen,
  setClearPendingCreativeChangesConfirmDialog,
  setCreativeDrawerData,
  setCreativeDrawerOpen,
} from '../../state/appSlice'
import {
  Button,
  CircularProgress,
  Drawer,
  FormControlLabel,
  IconButton,
  Switch,
  Tooltip,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useFragment, useMutation } from 'react-relay'
import MetaAdPreviewWrapper from './MetaAdPreviewWrapper'
import { graphql } from 'relay-runtime'
import CommentList from '../comment/CommentList'
import AddComment from '../comment/AddComment'
import { CreativeDrawerMetaAd$key } from './__generated__/CreativeDrawerMetaAd.graphql'
import {
  CreativeDrawerUser$data,
  CreativeDrawerUser$key,
} from './__generated__/CreativeDrawerUser.graphql'
import MetaAdPreview from './MetaAdPreview'
import CreativeTextForm from './CreativeTextForm'
import { CreativeDrawerCreateMetaAdMutation } from './__generated__/CreativeDrawerCreateMetaAdMutation.graphql'
import { CreativeDrawerCreateVisualAssetMutation } from './__generated__/CreativeDrawerCreateVisualAssetMutation.graphql'
import { CreativeDrawerUpdateVisualAssetMutation } from './__generated__/CreativeDrawerUpdateVisualAssetMutation.graphql'
import {
  CreativeDrawerGetPreSignedUrlMutation,
  CreativeDrawerGetPreSignedUrlMutation$data,
} from './__generated__/CreativeDrawerGetPreSignedUrlMutation.graphql'
import { toast } from 'react-toastify'
import { theme } from '../../mui'
import { CreativeDrawerApproveMutation } from './__generated__/CreativeDrawerApproveMutation.graphql'
import { ClearPendingCreativeChangesConfirmDialogContainer } from './ClearPendingCreativeChangesConfirmDialog'
import { getMetaAdListQueryVariables } from '../../pages/HomeRoute'
import CreativeAdvisor from './CreativeAdvisor'
import { CreativeDrawerBrand$key } from './__generated__/CreativeDrawerBrand.graphql'
import { useGlobalState } from '../../state/v2/store'
import { useTypeParam } from '../../state/url/type'
import { useTrackingContext } from '../../analytics/useTracking'
import { trackAdApproved, trackAdDraftCreated } from '../../analytics/tracking'

export enum CreativeDrawerView {
  COMMENTS,
  TEXT,
}

export interface CreativeDrawerState {
  insightText?: string
  metaAdFragmentRequest: CreativeDrawerMetaAd$key | null
  view: CreativeDrawerView
}

interface CreativeDrawerProps extends CreativeDrawerState {
  brandFragmentRequest: CreativeDrawerBrand$key
  open: boolean
  user: CreativeDrawerUser$data
}

export const creativeDrawerBrandFragment = graphql`
  fragment CreativeDrawerBrand on Brand {
    id
    ...CreativeAdvisorBrand
    ...CreativeTextFormBrand
  }
`

export const creativeDrawerMetaAdFragment = graphql`
  fragment CreativeDrawerMetaAd on MetaAd {
    id
    approved
    created
    submittedForApproval
    comments {
      id
    }
    visualAssets {
      id
    }
    ...CreativeAdvisorMetaAd
    ...CreativeTextForm
    ...CommentList
    ...MetaAdPreview
  }
`

export const creativeDrawerUserFragment = graphql`
  fragment CreativeDrawerUser on User {
    ...CreativeTextFormUser
    ...AddComment
  }
`

const getPreSignedUrlMutation = graphql`
  mutation CreativeDrawerGetPreSignedUrlMutation(
    $input: GetPreSignedUrlRequest!
  ) {
    getPreSignedUrl(input: $input)
  }
`

const createMetaAdMutation = graphql`
  mutation CreativeDrawerCreateMetaAdMutation(
    $input: CreateMetaAdRequest!
    $metaAdsFilter: MetaAdsFilter
  ) {
    createMetaAd(input: $input, metaAdsFilter: $metaAdsFilter) {
      id
      ...CreativeDrawerMetaAd
      brand {
        id
        draftMetaAdCount
        unapprovedMetaAdCount
        ...SubmitCreativeForApprovalConfirmDialogBrand
        ...CreativeList
      }
    }
  }
`

const createVisualAssetMutation = graphql`
  mutation CreativeDrawerCreateVisualAssetMutation(
    $input: CreateVisualAssetRequest!
    $metaAdsFilter: MetaAdsFilter
  ) {
    createVisualAsset(input: $input, metaAdsFilter: $metaAdsFilter) {
      id
      metaAd {
        id
        ...CreativeDrawerMetaAd
        brand {
          id
          ...SubmitCreativeForApprovalConfirmDialogBrand
          ...CreativeList
        }
      }
    }
  }
`

const updateVisualAssetMutation = graphql`
  mutation CreativeDrawerUpdateVisualAssetMutation(
    $input: UpdateVisualAssetRequest!
    $metaAdsFilter: MetaAdsFilter
  ) {
    updateVisualAsset(input: $input, metaAdsFilter: $metaAdsFilter) {
      id
      metaAd {
        id
        ...CreativeDrawerMetaAd
        brand {
          id
          ...SubmitCreativeForApprovalConfirmDialogBrand
          ...CreativeList
        }
      }
    }
  }
`

export const approveMutation = graphql`
  mutation CreativeDrawerApproveMutation(
    $input: UpdateMetaAdRequest!
    $metaAdsFilter: MetaAdsFilter
  ) {
    updateMetaAd(input: $input, metaAdsFilter: $metaAdsFilter) {
      id
      ...CreativeDrawerMetaAd
      brand {
        id
        approvedMetaAdCount
        unapprovedMetaAdCount
        ...SubmitCreativeForApprovalConfirmDialogBrand
        ...CreativeList
      }
    }
  }
`

interface RightSideHeaderProps {
  open: boolean
}

const S = {
  Wrapper: styled.div`
    margin: 1rem 2rem;
  `,
  Header: styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 1.5rem;
    margin: 1rem 1rem;
  `,
  Body: styled.div`
    display: flex;
    & > * {
      margin: 0 1rem;
    }
  `,
  UploadButton: styled(Button)`
    float: right;
    margin-top: 1rem;
  `,
  ApprovalSlider: styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    margin-top: 1rem;
  `,
  RightSideHeader: styled.div<RightSideHeaderProps>`
    display: flex;
    justify-content: space-between;
    margin: ${(props) => (props.open ? '.5rem 1rem 1rem 0' : '0')};
    height: ${(props) => (props.open ? '1.25rem' : '0')};
    overflow: hidden;
    transition: 0.5s;
  `,
  ViewCommentOrText: styled.div`
    color: ${theme.palette.primary.main};
    cursor: pointer;
  `,
}

const images = ['png', 'jpg', 'jpeg', 'gif']
const _URL = window.URL || window.webkitURL

const CreativeDrawer = ({
  user,
  view,
  open,
  brandFragmentRequest,
  metaAdFragmentRequest,
  insightText,
}: CreativeDrawerProps) => {
  const brand = useFragment(creativeDrawerBrandFragment, brandFragmentRequest)
  const metaAd = useFragment(
    creativeDrawerMetaAdFragment,
    metaAdFragmentRequest
  )
  const dispatch = useAppDispatch()
  const fileUploadInput = useRef(null as HTMLInputElement | null)
  const [uploadingData, setUploadingData] = useState(false)
  const [pendingFormChanges, setPendingFormChanges] = useState(false)
  const [createMetaAdRequest] =
    useMutation<CreativeDrawerCreateMetaAdMutation>(createMetaAdMutation)
  const [createVisualAssetRequest] =
    useMutation<CreativeDrawerCreateVisualAssetMutation>(
      createVisualAssetMutation
    )
  const [updateVisualAssetRequest] =
    useMutation<CreativeDrawerUpdateVisualAssetMutation>(
      updateVisualAssetMutation
    )
  const [getPreSignedUrlRequest] =
    useMutation<CreativeDrawerGetPreSignedUrlMutation>(getPreSignedUrlMutation)
  const [approveRequest, approveLoading] =
    useMutation<CreativeDrawerApproveMutation>(approveMutation)

  const brandId = useGlobalState((b) => b.brand?.map((brand) => brand.id).join(","))
  const [type] = useTypeParam()
  const ctx = useTrackingContext()

  useEffect(() => {
    if (!open) {
      setPendingFormChanges(false)
    }
  }, [open, setPendingFormChanges])

  const close = () => {
    if (pendingFormChanges) {
      dispatch(setClearPendingCreativeChangesConfirmDialog({ open: true }))
    } else {
      dispatch(setCreativeDrawerOpen(false))
    }
  }

  const accept: string[] = ['png', 'jpg', 'jpeg', 'mp4', 'gif']
  const images: string[] = ['png', 'jpg', 'jpeg', 'gif']

  const fileUploadHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target!.files![0]
    const extension = file.name.split('.').pop()!
    if (accept.indexOf(`${extension}`) === -1) {
      toast.error('Invalid file type')
      return
    }
    const contentType =
      images.indexOf(extension) > -1
        ? `image/${extension}`
        : `video/${extension}`
    setUploadingData(true)

    let validateMediaPromise: Promise<any> = Promise.resolve()
    if (images.indexOf(extension.toLowerCase()) !== -1) {
      validateMediaPromise = new Promise((resolve) => {
        const image = new Image()
        image.onload = () => resolve(image)
        image.src = _URL.createObjectURL(file)
      }).then((response) => {
        const image = response as HTMLImageElement
        if (image.height < 600 || image.width < 600) {
          setUploadingData(false)
          toast.error('Image must be at least 600x600')
          throw new Error()
        }
      })
    } else {
      validateMediaPromise = new Promise((resolve) => {
        console.log('loading video')
        const video = document.createElement('video')
        video.addEventListener('loadedmetadata', (event) => resolve(event))
        const source = document.createElement('source')
        video.appendChild(source)
        console.log(file)
        let codecs = ''
        if (file.type.indexOf('mp4') !== -1) {
          codecs = 'avc1'
        } else if (file.type.indexOf('quicktime') !== -1) {
          codecs = 'quicktime'
        }
        source.type = `${file.type}; codecs=${codecs}`
        source.src = _URL.createObjectURL(file)
        console.log(video)
      })
        .then((response) => {
          console.log('loaded video')
          const event = response as Event
          const video = event.target as HTMLVideoElement
          if (video.videoWidth < 1080 || video.videoHeight < 1080) {
            setUploadingData(false)
            toast.error('Video must be at least 1080x1080')
            throw new Error()
          } else if (video.duration < 1) {
            setUploadingData(false)
            toast.error('Video must be at least 1 second long')
            throw new Error()
          }
        })
        .catch((error) => console.log(error))
    }
    validateMediaPromise.then(() => {
      let metaAdIdPromise: Promise<string>
      if (metaAd == null) {
        metaAdIdPromise = validateMediaPromise.then(
          () =>
            new Promise((resolve, reject) => {
              if (!brandId || !type) {
                return resolve('')
              }
              return createMetaAdRequest({
                variables: {
                  input: {
                    brandId,
                  },
                  ...getMetaAdListQueryVariables(type),
                },
                onCompleted: (response) => {
                  dispatch(
                    setCreativeDrawerData({
                      metaAdFragmentRequest: response.createMetaAd,
                      view,
                    })
                  )
                  trackAdDraftCreated({ ad_id: response.createMetaAd.id, brand_id: brand.id, account_id: ctx.accountId }, ctx)
                  resolve(response.createMetaAd.id)
                },
                onError: reject,
              })
            })
        )
      } else {
        metaAdIdPromise = validateMediaPromise.then(() => metaAd.id)
      }
      const preSignedUrlPromise: Promise<CreativeDrawerGetPreSignedUrlMutation$data> =
        metaAdIdPromise.then(
          (metaAdId) =>
            new Promise((resolve, reject) =>
              getPreSignedUrlRequest({
                variables: {
                  input: {
                    adId: metaAdId,
                    extension,
                    platform: 'META',
                  },
                },
                onCompleted: resolve,
                onError: reject,
              })
            )
        )
      const uploadPromise = preSignedUrlPromise
        .then((response) =>
          fetch(response.getPreSignedUrl, {
            method: 'PUT',
            body: event.target!.files![0],
            headers: {
              'Content-Type': contentType,
            },
          })
        )
        .then((response) => {
          if (response.status >= 300 || response.status < 200) {
            throw new Error('Error uploading file')
          }
        })
      return Promise.all([metaAdIdPromise, preSignedUrlPromise, uploadPromise])
        .then(([metaAdId, preSignedUrl]) => {
          if (metaAd == null || metaAd.visualAssets.length === 0) {
            return new Promise((resolve, reject) =>
              createVisualAssetRequest({
                variables: {
                  input: {
                    metaAdId: metaAdId,
                    platform: 'META',
                    preSignedUrl: preSignedUrl.getPreSignedUrl,
                  },
                },
                onCompleted: resolve,
                onError: reject,
              })
            )
          } else {
            return new Promise((resolve, reject) =>
              updateVisualAssetRequest({
                variables: {
                  input: {
                    id: metaAd.visualAssets[0].id,
                    preSignedUrl: preSignedUrl.getPreSignedUrl,
                  },
                },
                onCompleted: resolve,
                onError: reject,
              })
            )
          }
        })
        .then(() => {
          setUploadingData(false)
          fileUploadInput.current!.value = ''
          toast.success('Successfully updated ad.')
        })
        .catch((error) => {
          console.log(error)
          setUploadingData(false)
          fileUploadInput.current!.value = ''
          toast.error('Error uploading file.')
        })
    })
  }

  const handleViewCommentOrTextClick = () => {
    dispatch(
      setCreativeDrawerData({
        metaAdFragmentRequest,
        view:
          view === CreativeDrawerView.COMMENTS
            ? CreativeDrawerView.TEXT
            : CreativeDrawerView.COMMENTS,
      })
    )
  }
  let rightContent
  if (view === CreativeDrawerView.COMMENTS) {
    rightContent = (
      <>
        <CommentList fragmentRequest={metaAd} />
        <AddComment fragmentRequest={user} metaAdId={metaAd!.id} />
      </>
    )
  } else {
    rightContent = (
      <>
        <CreativeTextForm
          brandFragmentRequest={brand}
          metaAdFragmentRequest={metaAd}
          userFragmentRequest={user}
          setPendingChanges={(pendingChanges: boolean) =>
            setPendingFormChanges(pendingChanges)
          }
          drawerOpen={open}
        />
      </>
    )
  }
  const commentCount = metaAd?.comments.length
  const uploadButton = view === CreativeDrawerView.TEXT && (
    <Tooltip
      title={
        'Please upload a JPG/JPEG/PNG image with dimensions of at least 1080x1080 or a MP4/GIF video with dimensions of at least 1080x1080 and a duration of at least 1 second'
      }
    >
      <S.UploadButton
        disabled={uploadingData}
        onClick={() => fileUploadInput.current!.click()}
        variant="outlined"
      >
        {uploadingData && (
          <CircularProgress size="1rem" style={{ marginRight: '0.5rem' }} />
        )}
        Upload
      </S.UploadButton>
    </Tooltip>
  )
  const handleApprovalChange = () => {
    const onCompleted = () => {
      toast.success(
        `Successfully ${metaAd?.approved ? 'unapproved' : 'approved'} ad.`
      )
      trackAdApproved({ ad_id: metaAd?.id ?? '', brand_id: brand.id, account_id: ctx.accountId, is_approved: !metaAd?.approved }, ctx)
      dispatch(setCampaignModalOpen(false))
    }
    const onError = (error: Error) => {
      console.log(error)
      toast.error(`Unable to approve ad.`)
    }
    approveRequest({
      variables: {
        input: {
          id: metaAd!.id,
          approved: !metaAd?.approved,
        },
        ...getMetaAdListQueryVariables(type),
      },
      onCompleted,
      onError,
    })
  }
  const approveSlider = metaAd?.submittedForApproval && !metaAd?.created && (
    <>
      <S.ApprovalSlider>
        <FormControlLabel
          control={
            <Switch
              checked={metaAd?.approved}
              onChange={handleApprovalChange}
              disabled={approveLoading}
            />
          }
          label="Ad Approved "
        />
      </S.ApprovalSlider>
    </>
  )
  let header
  if (metaAd == null) {
    header = 'Create Ad'
  } else if (metaAd.submittedForApproval) {
    header = 'Review Creative'
  } else {
    header = 'Edit Ad'
  }
  return (
    <>
      <ClearPendingCreativeChangesConfirmDialogContainer />
      <input
        ref={fileUploadInput}
        accept={accept.join(',')}
        type="file"
        name="file"
        style={{ visibility: 'hidden', display: 'none' }}
        onChange={fileUploadHandler}
      />
      <Drawer anchor="right" open={open} onClose={close}>
        <S.Wrapper>
          <S.Header>
            {header}
            <IconButton onClick={close}>
              <CloseIcon
                sx={{
                  color: theme.palette.primary.main,
                  fontSize: '1.75rem',
                }}
              />
            </IconButton>
          </S.Header>
          <S.Body>
            <CreativeAdvisor
              brandFragmentRequest={brand}
              metaAdFragmentRequest={metaAd}
            />
            <div>
              <MetaAdPreviewWrapper>
                {metaAd != null ? (
                  <MetaAdPreview fragmentRequest={metaAd} />
                ) : (
                  'Create Ad to Preview'
                )}
              </MetaAdPreviewWrapper>
              {uploadButton}
              {approveSlider}
            </div>
            <div>
              <S.RightSideHeader open={metaAd != null}>
                <div>
                  {commentCount} Comment
                  {commentCount === 1 ? '' : 's'}
                </div>
                {!metaAd?.approved && (
                  <S.ViewCommentOrText onClick={handleViewCommentOrTextClick}>
                    {view === CreativeDrawerView.COMMENTS
                      ? 'Edit Ad'
                      : 'View Comments'}
                  </S.ViewCommentOrText>
                )}
              </S.RightSideHeader>
              {rightContent}
            </div>
          </S.Body>
        </S.Wrapper>
      </Drawer>
    </>
  )
}

export default CreativeDrawer

interface CreativeDrawerContainerProps {
  brandFragmentRequest: CreativeDrawerBrand$key
}

export const CreativeDrawerContainer = (
  props: CreativeDrawerContainerProps
) => {
  const creativeDrawerData = useAppSelector(selectCreativeDrawerData)
  const open = useAppSelector(selectCreativeDrawerOpen)
  const userKey = useAppSelector(selectUserKey)
  const user = useFragment(
    creativeDrawerUserFragment,
    userKey as CreativeDrawerUser$key
  )
  if (creativeDrawerData == null) {
    return <></>
  }
  return (
    <CreativeDrawer
      open={open}
      {...props}
      {...creativeDrawerData}
      user={user}
    />
  )
}
